import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import { getPageCache, setPageCache } from "../utils/pageCache";

export const usePantry = () => {
  const { user } = useAuth();
  // Seed the count from the last fetch this session so remounts (back
  // navigation) render immediately; the fetch below refreshes silently.
  const cacheKey = user ? `pantry-count:${user.uid}` : null;
  const seeded = getPageCache<number>(cacheKey);
  const [pantryCount, setPantryCount] = useState<number>(seeded ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPantryData = useCallback(async (): Promise<void> => {
    if (!user) {
      setPantryCount(0);
      return;
    }

    if (getPageCache(`pantry-count:${user.uid}`) === undefined) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = await user.getIdToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch pantry data");
      }

      const data = await response.json();
      
      // Count items that are not in review state (approved items)
      const approvedItems = (data.items || []).filter((item: any) => !item.needsReview);
      setPantryCount(approvedItems.length);
      setPageCache(`pantry-count:${user.uid}`, approvedItems.length);
    } catch (error: any) {
      console.error("Error fetching pantry data:", error);
      setError(error.message);
      setPantryCount(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch data when user changes
  useEffect(() => {
    if (user) {
      fetchPantryData();
    } else {
      setPantryCount(0);
    }
  }, [user, fetchPantryData]);

  return {
    pantryCount,
    loading,
    error,
    refetchPantryData: fetchPantryData,
  };
};